/**
 * Lift2Fit — the movement picker.
 *
 * Reused by the live Grid ("add a movement to this session") and the programme
 * builder/editor ("add an exercise to this day"). It reads the same
 * `GET /exercises` catalogue as the goal picker and filters client-side, so it
 * keeps working whether the API honours `search` or ignores it.
 */
import { useEffect, useMemo, useState } from 'react';
import { api, type ExerciseOption } from '@/lib/api';
import { cn } from '@/lib/format';
import { CloseIcon, PlusIcon } from '@/lib/icons';
import { StatusChip } from './components';

export interface MovementPickerProps {
  onPick: (exercise: ExerciseOption) => void;
  onClose: () => void;
  title?: string;
  /** shown when the account reached the API; a demo answer says so */
  demoNotice?: string;
}

export function MovementPicker({ onPick, onClose, title = 'Add a movement' }: MovementPickerProps) {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<ExerciseOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [fromDemo, setFromDemo] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .exercises()
      .then((result) => {
        if (!alive) return;
        setOptions(result.data);
        setFromDemo(result.source === 'demo');
        setLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setOptions([]);
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options.slice(0, 60);
    return options
      .filter((option) => `${option.name} ${option.slug}`.toLowerCase().includes(needle))
      .slice(0, 60);
  }, [options, query]);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-brand-charcoal/50 p-0 sm:items-center sm:p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid="movement-picker"
        onClick={(event) => event.stopPropagation()}
        className="flex max-h-[85vh] w-full max-w-[560px] flex-col rounded-card border border-hairline bg-surface shadow-modal"
      >
        <header className="flex items-start justify-between gap-3 border-b border-hairline px-5 py-4">
          <div className="flex flex-col gap-1">
            <h2 className="font-display text-[18px] font-black uppercase tracking-[-0.01em] text-ink">{title}</h2>
            <p className="text-[12px] text-ink-muted">
              {loading ? 'Loading the catalogue…' : `${options.length} movements in the catalogue.`}
              {fromDemo ? ' Offline: showing the built-in list.' : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close the movement picker"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center border border-hairline text-ink hover:border-accent hover:text-accent"
          >
            <CloseIcon />
          </button>
        </header>

        <div className="border-b border-hairline px-5 py-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Search movements</span>
            <input
              type="search"
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Bench, squat, row…"
              data-testid="movement-search"
              className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink placeholder:text-ink-muted"
            />
          </label>
        </div>

        <ul className="flex min-h-0 flex-1 flex-col overflow-y-auto" aria-label="Movements">
          {filtered.map((option) => (
            <li key={option.id} className="border-b border-hairline last:border-b-0">
              <button
                type="button"
                data-testid="movement-option"
                onClick={() => onPick(option)}
                className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left hover:bg-page-subtle"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="text-[14px] font-bold text-ink">{option.name}</span>
                  <span className="l2f-tabular font-mono text-[11px] text-ink-muted">{option.slug}</span>
                </span>
                <PlusIcon className="shrink-0 text-accent" />
              </button>
            </li>
          ))}
          {!loading && filtered.length === 0 ? (
            <li className="px-5 py-6">
              <StatusChip tone="warning">No movement matches “{query.trim()}”</StatusChip>
            </li>
          ) : null}
        </ul>

        <footer className={cn('border-t border-hairline px-5 py-3 text-[12px] text-ink-muted', loading && 'opacity-60')}>
          Client-side filter — the catalogue is fetched once and never re-queried per keystroke.
        </footer>
      </div>
    </div>
  );
}
